import React, {FormEvent, MouseEvent, ReactElement, ReactNode, useEffect, useState} from 'react';

import {lighten} from 'polished';
import {useTranslation} from 'react-i18next';

import {colors} from '@app/styles/theme';
import {InnerFilterValue} from '@shared/types/grid';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Icon} from '@shared/ui/Icon';
import {Popover} from '@shared/ui/Popover';

import {
    ButtonsContainer,
    CloseButton,
    FilterContent,
    FilterIconContainer,
    HeaderWrapper,
} from './styled';

interface WrapperProps {
    component: ReactElement;
    value: InnerFilterValue;
    onChange: (value: InnerFilterValue) => void;
    onReset: () => void;
}

const Wrapper = ({component, value, onChange, onReset}: WrapperProps) => {
    const {t} = useTranslation();
    const [innerValue, setInnerValue] = useState<InnerFilterValue>(value);

    useEffect(() => {
        setInnerValue(value);
    }, [value]);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        onChange(innerValue);
    };

    const handleReset = (e: MouseEvent) => {
        e.preventDefault();
        onReset();
    };

    const handleChange = (data: any) => {
        // TODO input field has bad structure of onChange parameter
        if (data && data.target != null) {
            setInnerValue(data.target.value);
        } else {
            setInnerValue(data);
        }
    };

    // TODO fix uncontrolled -> controlled warning
    // TODO integrate validation
    return (
        <form onSubmit={handleSubmit}>
            {React.cloneElement(component, {
                ...component.props,
                value: innerValue,
                onChange: handleChange,
            })}

            <ButtonsContainer>
                <Button
                    variant={ButtonVariant.Secondary}
                    type="button"
                    onClick={handleReset}
                    size="sm"
                >
                    {t('components.table.Reset')}
                </Button>
                <Button variant={ButtonVariant.Normal} type="submit" size="sm">
                    {t('components.table.Submit')}
                </Button>
            </ButtonsContainer>
        </form>
    );
};

interface Props extends WrapperProps {
    header: ReactNode;
    parentRef?: ReactElement;
}

const headerOffset = {x: 20, y: 5};

export const Filtration = ({header, component, onChange, onReset, value, parentRef}: Props) => {
    const [opened, setOpened] = useState(false);

    const handleChange = (value: InnerFilterValue) => {
        onChange(value);
        setOpened(false);
    };

    const handleReset = () => {
        onReset();
        setOpened(false);
    };

    return (
        <>
            <FilterIconContainer
                active={opened}
                color={value == null ? lighten(0.1, colors.textSecondary) : colors.textPrimary}
                onClick={() => setOpened(!opened)}
                name="filter"
                size={26}
            />

            <Popover
                isOpen={opened}
                onClose={() => setOpened(false)}
                // @ts-ignore
                pivotElement={parentRef}
                offset={headerOffset}
            >
                <FilterContent>
                    <CloseButton
                        icon={<Icon name="cross" color="#C4C4C4" size={12} />}
                        onClick={() => setOpened(false)}
                    />
                    <HeaderWrapper>{header}</HeaderWrapper>
                    <Wrapper
                        component={component}
                        value={value}
                        onChange={handleChange}
                        onReset={handleReset}
                    />
                </FilterContent>
            </Popover>
        </>
    );
};
